import React, { useEffect, useState, useRef, useCallback } from "react";
import { CalculateAge } from "../../../utils/CalculateAge";
import { fetchData } from "../../../helpers/fetchData";
import Flag from "react-world-flags";
import { useTranslation } from "react-i18next";
import { getCountryCode } from "../../../utils/CountryCodes";

function PatientListModal({
  data,
  handlePatientSelect,
  payload = {},
  setPayloadData = () => { },
  props,
  setListModalData = () => { },
  setOpenListModal = () => { },
  isSearch = false,
}) {
  const { data: initialData = [], ...paginationInfo } = data || {};
  const [patients, setPatients] = useState(
    Array.isArray(data) ? data : initialData
  );
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(paginationInfo || {});
  const listRef = useRef(null);

  const isFirstRender = useRef(true);

  const fetchPatients = async (isLoadMore = false) => {
    if (loading) return;
    setLoading(true);
    try {
      if (!payload) return;

      const response = await fetchData("POST", "v3/patient/filter", {
        contact: data?.contactNumber,
        ...payload,
      });

      if (isLoadMore) {
        setPatients((prev) => [...prev, ...(response?.data || [])]);
      } else {
        setPatients(response?.data || []);
      }
      setPagination(response || {});
    } catch (error) {
      console.error("Failed to fetch patients:", error);
      if (!isLoadMore) {
        setPatients([]);
        setPagination({});
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!Array.isArray(data)) {
      if ((payload?.page || 1) > 1 && payload?.page === pagination?.page) {
        return;
      }
      const isLoadMore = (payload?.page || 1) > 1;
      fetchPatients(isLoadMore);
    }
  }, [payload?.page, payload?.perPage]);

  const handleScroll = useCallback(() => {
    if (listRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        if (!loading && pagination.page < pagination.totalPages) {
          setPayloadData((prev) => ({ ...prev, page: (prev.page || 1) + 1 }));
        }
      }
    }
  }, [loading, pagination, setPayloadData]);


  const handlePatientClick = (patient) => {
    if (isSearch) {
      handlePatientSelect(patient);
    } else {
      handlePatientSelect(patient, props);
    }

    setPayloadData({ page: 1, perPage: 10 });
    setOpenListModal(false);
    setListModalData([]);
  };

  return (
    <div
      ref={listRef}
      onScroll={handleScroll}
      className="h-[385px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
    >
      {patients?.length > 0 ? (
        <>
          {patients.map((patient, index) => (
            <div
              key={`${patient?.id || index}-${index}`}
              className="p-4 mb-2 cursor-pointer hover:bg-primary border border-gray-200 hover:border-primary group transition-all duration-200 rounded-lg shadow-sm hover:shadow-md"
              onClick={() => handlePatientClick(patient)}
            >
              <div className="flex items-start justify-start gap-6">
                <div

                >
                  <Flag
                    code={getCountryCode(patient?.nationality || "Saudi")}
                    fallback={<span>{t("Unknown")}</span>}
                    className="h-12 w-12 xl:h-14 xl:w-14"
                  />
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex-1">
                    <h3 className="text-gray-900 font-semibold text-lg group-hover:text-white transition-colors">
                      {patient?.full_name || "N/A"}
                    </h3>
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      <span className="text-gray-600 group-hover:text-gray-100">
                        {t("Age")}:{" "}
                        <span className="font-medium">
                          {CalculateAge(patient?.dob) || "N/A"}
                        </span>
                      </span>
                      <span className="text-gray-400">•</span>
                      <span className="text-gray-600 group-hover:text-gray-100">
                        {t("MRN")}:{" "}
                        <span className="font-medium">
                          {patient?.file_no || "N/A"}
                        </span>
                      </span>
                      <span className="text-gray-400">•</span>
                      <span className="text-gray-600 group-hover:text-gray-100">
                        {t("Doc")}:{" "}
                        <span className="font-medium">
                          {patient?.document_id || "N/A"}
                        </span>
                      </span>
                      {patient?.old_file_no && (
                        <>
                          <span className="text-gray-400">•</span>
                          <span className="text-gray-600 group-hover:text-gray-100">
                            {t("Old MRN")}:{" "}
                            <span className="font-semibold text-red-500 group-hover:text-white">
                              {patient?.old_file_no}
                            </span>
                          </span>
                        </>
                      )}
                      <span className="text-gray-400">•</span>
                      <span className="text-gray-600 group-hover:text-gray-100">
                        {t("Nationality")}:{" "}
                        <span className="font-medium">
                          {patient?.nationality || "N/A"}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {loading && (
            <div className="text-center py-4 text-gray-500">
              {t("Loading more patients...")}
            </div>
          )}
        </>
      ) : (
        !loading && (
          <div className="text-center text-gray-500 py-8">
            <div className="text-4xl mb-2">🔍</div>
            <p className="font-medium">{t("No patients found")}</p>
            <p className="text-sm mt-1">
              {t("Try adjusting your search criteria")}
            </p>
          </div>
        )
      )}
    </div>
  );
}

export default PatientListModal;
